'use client';

import {
  BookOpen,
  Copy,
  ExternalLink,
  FileText,
  Hash,
  MessageSquare,
  MoreHorizontal,
  Shield,
  Sparkles,
  User as UserIcon,
  UserMinus,
  UserPlus,
  Volume2,
  VolumeX,
} from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import { useRelationshipStatus } from '@/components/relationships/RelationshipsProvider';
import { ProfileIdentityBadges } from '@/components/shared/ProfileIdentityBadges';
import { UserPreview } from '@/components/shared/previews/UserPreview';
import { RelationshipBadge } from '@/components/shared/RelationshipBadge';
import { RelativeTime } from '@/components/shared/RelativeTime';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTimelineContext } from '@/contexts/TimelineContext';
import { useAuth } from '@/hooks/useAuth';
import { useFollowActions } from '@/lib/hooks/api/social-actions';
import { buildUserProfileRoute } from '@/lib/routes/app';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { isAdmin } from '@/types/auth';
import type { TweetAuthor } from '@/types/tweet';

interface TweetHeaderProperties {
  author: TweetAuthor;
  canExplain?: boolean | undefined;
  canSummarize?: boolean | undefined;
  compact?: boolean | undefined;
  createdAt?: Date | null | undefined;
  isExplaining?: boolean | undefined;
  isMuted?: boolean | undefined;
  isSummarizingSummary?: boolean | undefined;
  onExplain?: (() => void) | undefined;
  onMenuOpenChange?: ((open: boolean) => void) | undefined;
  onOpenConversation?: (() => void) | undefined;
  onRefreshTweet?: (() => void) | undefined;
  onSummarize?: (() => void) | undefined;
  onToggleMute?: (() => void) | undefined;
  showDebugIds?: boolean | undefined;
  tweetId: string;
}

export function TweetHeader(properties: TweetHeaderProperties) {
  const {
    author,
    createdAt,
    tweetId,
    compact = false,
    isMuted = false,
    onToggleMute,
    onOpenConversation,
    onRefreshTweet,
    canSummarize = false,
    onSummarize,
    isSummarizingSummary = false,
    canExplain = false,
    onExplain,
    isExplaining = false,
    showDebugIds = false,
    onMenuOpenChange,
  } = properties;
  const initialRelativeNow = useMemo(() => Date.now(), []);
  const { user } = useAuth();
  const authorId = author.id;
  const authorUsername = author.username;
  const authorName = author.name || author.username;
  const authorVerifiedType = author.verifiedType;
  const authorAffiliations = author.affiliations ?? undefined;
  const isSelf = (user?.twitterUsername ?? null) === authorUsername;
  const isAdminUser = isAdmin(user ? { role: user.role ?? null } : null);
  const rel = useRelationshipStatus(authorId);
  const timelineContext = useTimelineContext();
  const hideFollowingBadge = Boolean(timelineContext);
  const handleOpenInX = () => {
    const url = `https://twitter.com/${authorUsername}/status/${tweetId}`;
    if (globalThis.window !== undefined) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyAuthorId = () => {
    if (!authorId) return;
    copyToClipboard(authorId).catch(() => {
      /* ignore clipboard errors */
    });
  };

  const handleCopyTweetId = () => {
    if (tweetId === undefined) return;
    copyToClipboard(tweetId).catch(() => {
      /* ignore clipboard errors */
    });
  };

  const date = createdAt ?? null;
  const isValidDate = date instanceof Date && !Number.isNaN(date.getTime());

  // Follow/Unfollow state and action for this author
  const derivedFollowState = rel ? rel.type === 'mutual' || rel.type === 'following' : null;
  const {
    isFollowing,
    isLoading: isFollowLoading,
    toggleFollow,
  } = useFollowActions(authorId, authorUsername, derivedFollowState);

  return (
    <div className="flex items-start justify-between gap-2">
      <div
        className={cn(
          'flex items-center gap-1 min-w-0 flex-wrap gap-y-0.5',
          compact ? 'text-xs' : 'text-[13px] sm:text-sm'
        )}
      >
        <UserPreview username={authorUsername}>
          <Link
            href={buildUserProfileRoute(authorUsername)}
            className="font-semibold hover:underline text-foreground break-words"
          >
            {authorName}
          </Link>
        </UserPreview>

        <ProfileIdentityBadges
          verifiedType={authorVerifiedType}
          affiliations={authorAffiliations}
          size={compact ? 'sm' : 'md'}
          renderAffiliation={({ username: affiliationUsername, affiliation, content }) => {
            const organizationProfileId = affiliation.organizationProfileId;
            if (organizationProfileId === undefined && !affiliationUsername) {
              return content;
            }
            return (
              <UserPreview profileId={organizationProfileId} username={affiliationUsername ?? ''}>
                {content}
              </UserPreview>
            );
          }}
        />

        <UserPreview username={authorUsername}>
          <Link
            href={buildUserProfileRoute(authorUsername)}
            className="text-muted-foreground hover:underline break-words"
          >
            @{authorUsername}
          </Link>
        </UserPreview>

        {rel && !(hideFollowingBadge && rel.type === 'following') ? (
          <RelationshipBadge
            type={rel.type}
            size={compact ? 'xs' : 'sm'}
            subjectId={authorId}
            subjectUsername={authorUsername}
          />
        ) : null}

        <span className="text-muted-foreground">·</span>

        <a
          href={`https://twitter.com/${authorUsername}/status/${tweetId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:underline whitespace-nowrap"
          aria-label="View tweet on Twitter"
        >
          {isValidDate ? (
            <RelativeTime
              date={date}
              initialNowMs={initialRelativeNow}
              style="long"
              display="compact"
              minimumUpdateIntervalMs={60_000}
              className="whitespace-nowrap"
            />
          ) : (
            <span>Unknown time</span>
          )}
        </a>
      </div>

      <DropdownMenu {...(onMenuOpenChange ? { onOpenChange: onMenuOpenChange } : {})}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-5 w-7 -mr-2', !compact && 'sm:h-5.5 sm:w-8 sm:-mr-0')}
            aria-label="More options"
          >
            <MoreHorizontal className={cn('h-3.5 w-3.5', !compact && 'sm:h-4 sm:w-4')} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isSelf && (
            <DropdownMenuItem onClick={toggleFollow} disabled={isFollowLoading}>
              {isFollowing ? (
                <>
                  <UserMinus className="h-4 w-4" aria-hidden="true" />
                  <span>{`Unfollow @${authorUsername}`}</span>
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" aria-hidden="true" />
                  <span>{`Follow @${authorUsername}`}</span>
                </>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleOpenInX}>
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            <span>Open in X</span>
          </DropdownMenuItem>
          {canSummarize && onSummarize ? (
            <DropdownMenuItem onClick={onSummarize} disabled={isSummarizingSummary}>
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span>{isSummarizingSummary ? 'Summarizing…' : 'Summarize'}</span>
            </DropdownMenuItem>
          ) : null}
          {canExplain && onExplain ? (
            <DropdownMenuItem onClick={onExplain} disabled={isExplaining}>
              <BookOpen className="h-4 w-4" aria-hidden="true" />
              <span>{isExplaining ? 'Explaining…' : 'Explain'}</span>
            </DropdownMenuItem>
          ) : null}
          {isAdminUser && (
            <DropdownMenuItem asChild>
              <Link href={`/tweet/${tweetId}`} prefetch={false}>
                <FileText className="h-4 w-4" aria-hidden="true" />
                <span>Open tweet page</span>
              </Link>
            </DropdownMenuItem>
          )}
          {onOpenConversation && (
            <DropdownMenuItem onClick={onOpenConversation}>
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              <span>Open conversation</span>
            </DropdownMenuItem>
          )}
          {onToggleMute && (
            <DropdownMenuItem onClick={onToggleMute}>
              {isMuted ? (
                <>
                  <Volume2 className="h-4 w-4" aria-hidden="true" />
                  <span>{`Unmute @${authorUsername}`}</span>
                </>
              ) : (
                <>
                  <VolumeX className="h-4 w-4" aria-hidden="true" />
                  <span>{`Mute @${authorUsername}`}</span>
                </>
              )}
            </DropdownMenuItem>
          )}
          {onRefreshTweet && <DropdownMenuSeparator />}
          {onRefreshTweet && (
            <DropdownMenuItem onClick={onRefreshTweet}>
              <Shield className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>Refresh from Twitter</span>
            </DropdownMenuItem>
          )}
          {showDebugIds ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCopyAuthorId} className="focus:bg-transparent justify-between">
                <span className="flex items-center gap-2 font-mono text-sm text-primary select-all">
                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                  {authorId}
                </span>
                <Copy className="h-4 w-4 text-muted-foreground" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyTweetId} className="focus:bg-transparent justify-between">
                <span className="flex items-center gap-2 font-mono text-sm text-primary select-all">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  {tweetId}
                </span>
                <Copy className="h-4 w-4 text-muted-foreground" />
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
